/**
 * PaymentMethodsComponent
 * Reusable trust component displaying accepted payment method logos.
 *
 * Usage:
 *   <PaymentMethods />                          — default centered with label
 *   <PaymentMethods showLabel={false} />         — logos only, no label
 *   <PaymentMethods align="left" />              — left-aligned
 *   <PaymentMethods variant="light" />           — for dark backgrounds
 *   <PaymentMethods size="sm" />                 — compact version
 *
 * To add new payment methods, append to the METHODS array below.
 */

const METHODS = [
  { name: "Visa", src: "https://cdn.candopayments.com/payments/visa_colour_web_transparent.svg" },
  { name: "Mastercard", src: "https://cdn.candopayments.com/payments/mastercard_colour_web_transparent.svg" },
  { name: "American Express", src: "https://cdn.candopayments.com/payments/amex_colour_web_transparent.svg" },
  { name: "Apple Pay", src: "https://cdn.candopayments.com/payments/apple-pay_black_web_transparent.svg", invertOnDark: true },
  {
    name: "Google Pay",
    svg: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 435.97 173.13" className="h-full w-auto">
        <path d="M206.2 84.58v50.75h-16.1V8h42.7a38.61 38.61 0 0 1 27.65 10.85A34.88 34.88 0 0 1 272 45.35a35.15 35.15 0 0 1-11.55 26.6 38.2 38.2 0 0 1-27.65 10.63zm0-60.48v44.38h27a23.4 23.4 0 0 0 17.68-7.35 23.91 23.91 0 0 0 0-29.5 22.82 22.82 0 0 0-17.67-7.53z" fill="#5f6368" />
        <path d="M309.1 46.78c11.9 0 21.26 3.15 28.08 9.45s10.22 14.88 10.22 25.73v52h-15.4v-11.73h-.7c-6.65 9.8-15.49 14.7-26.51 14.7a36.36 36.36 0 0 1-25-8.93 28.71 28.71 0 0 1-10.15-22.4c0-9.45 3.58-17 10.73-22.58s16.8-8.4 29.05-8.4a52.08 52.08 0 0 1 23.62 5.08v-3.5a19.79 19.79 0 0 0-7.35-15.58 25.15 25.15 0 0 0-17.15-6.47c-9.98.03-17.85 4.23-23.63 12.6l-14.18-8.93c8.58-12.42 21.18-18.62 37.8-18.62zm-20.83 62.3a14.4 14.4 0 0 0 5.95 11.73 21.22 21.22 0 0 0 13.65 4.73 27.47 27.47 0 0 0 19.25-8.05c5.78-5.37 8.68-11.73 8.68-19.08a40.47 40.47 0 0 0-21-5.6c-8.93 0-16.36 2.19-22.31 6.56s-4.22 5.78-4.22 9.71z" fill="#5f6368" />
        <path d="M436 49.75l-53.55 123.38H365.9l19.88-43.23-35.23-80.15h17.5l25.2 60.83h.35l24.5-60.83z" fill="#5f6368" />
        <path d="M141.14 73.64A85.79 85.79 0 0 0 139.9 59H72v27.73h38.89a33.33 33.33 0 0 1-14.38 21.88v17.96h23.21c13.59-12.53 21.42-31.06 21.42-52.93z" fill="#4285f4" />
        <path d="M72 144.75c19.43 0 35.79-6.38 47.72-17.38l-23.21-17.96c-6.47 4.38-14.78 6.88-24.51 6.88-18.78 0-34.72-12.66-40.42-29.72H7.67v18.55A72 72 0 0 0 72 144.75z" fill="#34a853" />
        <path d="M31.58 86.57a43.64 43.64 0 0 1 0-27.8V40.22H7.67a72.08 72.08 0 0 0 0 64.9z" fill="#fbbc04" />
        <path d="M72 28.95a39 39 0 0 1 27.56 10.76l20.57-20.57A69.18 69.18 0 0 0 72 .75 72 72 0 0 0 7.67 40.22l23.91 18.55C37.28 41.62 53.22 28.95 72 28.95z" fill="#ea4335" />
      </svg>
    ),
  },
];

const ALIGN_CLASSES = {
  center: "justify-center",
  left: "justify-start",
  right: "justify-end",
};

const SIZE_HEIGHTS = {
  sm: "h-5",
  md: "h-7",
  lg: "h-9",
};

export default function PaymentMethods({
  showLabel = true,
  align = "center",
  variant = "default",
  size = "md",
}) {
  const labelColor = variant === "light" ? "text-white/50" : "text-brand-grey";
  const logoOpacity = variant === "light" ? "opacity-70 hover:opacity-100" : "opacity-80 hover:opacity-100";
  const height = SIZE_HEIGHTS[size] || SIZE_HEIGHTS.md;

  return (
    <div data-testid="payment-methods" role="group" aria-label="Accepted payment methods">
      {showLabel && (
        <p className={`text-[10px] uppercase tracking-widest font-semibold ${labelColor} text-center mb-3`}>
          Accepted payment methods
        </p>
      )}
      <div className={`flex flex-nowrap items-center gap-4 sm:gap-7 ${ALIGN_CLASSES[align] || ALIGN_CLASSES.center}`}>
        {METHODS.map((m) => (
          <div
            key={m.name}
            className={`${height} transition-opacity duration-200 ${logoOpacity}`}
            title={m.name}
          >
            {m.svg ? (
              <div className={`${height} w-auto`} role="img" aria-label={m.name}>
                {m.svg}
              </div>
            ) : (
              <img
                src={m.src}
                alt={m.name}
                width="200"
                height="51"
                className={`${height} w-auto ${variant === "light" && m.invertOnDark ? "brightness-0 invert" : ""}`}
                loading="lazy"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
